import React, { useEffect, useRef } from 'react';
import { observer, Provider } from 'mobx-react';
import {
  Button,
  Modal,
  ModalVariant,
  Text,
  TextContent,
  TextVariants,
} from '@patternfly/react-core';
import { useTranslation, Trans } from 'react-i18next';
import { QuotaSlug } from 'types';
import type { UserLab } from 'types/labs';
import useMobxStores from 'hooks/useMobxStores';
import type UserStore from 'stores/User';
import {
  LAB_DATA_POLLING_SECONDS,
  LAB_AUTOSHUTDOWN_POLLING_SECONDS,
} from 'config/constants';

import LabControls from './LabControls';
import LabInstructions from './LabControls/LabInstructions';
import LabAlert from './LabControls/LabAlert';
import AutostopTimer from './LabControls/AutostopTimer';
import DynamicLaunchParameter from './LabControls/DynamicLaunchParameter';
import LabWebApps from './LabControls/LabWebAppPanel';
import {
  LabCard,
  LabCardBody,
  LabCardFooter,
  LabCardResources,
  LabCardAutoMgmt,
} from './LabCard';
import LabEnvironmentStore, { labEnvironmentContext } from './store';
import type {
  ClassDashboardStore,
  Enrollment,
  LabDriverComponentProps,
} from './types';
import './LabCard/styles.scss';
import DefaultLabComponent from './DefaultLabComponent';
import OpenstackLabComponent from './OpenstackLabComponent';
import OCPSharedLabComponent from './OCPSharedLabComponent';
import TowerLabComponent from './TowerLabComponent';
import PTLLabComponent from './PTLLabComponent';
import './styles.scss';

type LabEnvironmentProps = {
  courseSlug: string;
  labDefinition: Record<string, unknown>;
  currentEnrollment?: Enrollment;
  targetEnrollment?: Enrollment;
  classDashboardStore?: ClassDashboardStore;
  userStore?: UserStore;
};

const componentMap: Record<
  string,
  React.ComponentType<LabDriverComponentProps>
> = {
  shared_ocp: OCPSharedLabComponent,
  tower: TowerLabComponent,
  openstack: OpenstackLabComponent,
  ptl: PTLLabComponent,
};

const LabEnvironment: React.FC<LabEnvironmentProps> = ({
  courseSlug,
  labDefinition,
  currentEnrollment = {},
  targetEnrollment = {},
  classDashboardStore,
  userStore: userStoreProp,
}) => {
  const { t } = useTranslation();
  const { userStore: contextUserStore, uiStore } = useMobxStores();
  const userStore = userStoreProp ?? contextUserStore;
  const labHoursWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const storeRef = useRef<LabEnvironmentStore>();
  if (!storeRef.current) {
    storeRef.current = new LabEnvironmentStore(userStore, classDashboardStore);
    storeRef.current.currentEnrollment = currentEnrollment;
    storeRef.current.targetEnrollment = targetEnrollment;
    storeRef.current.labDefinition = labDefinition;
    storeRef.current.courseSlug = courseSlug;
  }
  const store = storeRef.current;
  const userLab = store.userLab as UserLab;

  const prevCourseSlugRef = useRef(courseSlug);

  const updateStore = () => {
    store.currentEnrollment = currentEnrollment;
    store.targetEnrollment = targetEnrollment;
    store.labDefinition = labDefinition;
    store.courseSlug = courseSlug;
    store.lastRequestedCommand = null;
    store.userLab = {};
    store.fetchUserLab();
    store.setInhibitLabInfo();
  };

  const checkLabQuota = () => {
    labHoursWatchdogRef.current = setTimeout(() => {
      userStore
        .getQuotasUsage(QuotaSlug.LabHours)
        .then(() => {
          const currentUserLab = store.userLab as UserLab;
          if (
            !userStore.hasLabHours &&
            currentUserLab.state === 'running' &&
            currentUserLab.desired_state !== 'stopped'
          ) {
            store.doCommand('app_stop', null);
            userStore.getEnablements();

            if (labHoursWatchdogRef.current) {
              clearTimeout(labHoursWatchdogRef.current);
            }

            store.createNewModal(
              {
                title: t(
                  'Your labs have shut down. You have used all of your available lab hours.',
                ),
                dismissText: t('Ok'),
              },
              true,
            );
          }
        })
        .catch((error: unknown) => {
          console.error(error);
        })
        .finally(() => {
          checkLabQuota();
        });
    }, LAB_AUTOSHUTDOWN_POLLING_SECONDS * 1000);
  };

  useEffect(() => {
    if (prevCourseSlugRef.current !== courseSlug) {
      prevCourseSlugRef.current = courseSlug;
      store.resetLabInformation();
      updateStore();
      return;
    }

    store.currentEnrollment = currentEnrollment;
    store.targetEnrollment = targetEnrollment;
    store.labDefinition = labDefinition;
  }, [courseSlug, currentEnrollment, labDefinition, store, targetEnrollment]);

  useEffect(() => {
    store.pollIntervalSecs = LAB_DATA_POLLING_SECONDS;

    if (!(currentEnrollment.uuid || targetEnrollment.uuid)) {
      if (userStore.enforceLabLimit) {
        checkLabQuota();
      }
      store.setInhibitLabInfo();
    }

    if (userStore.isLabPowerUser) {
      store.getOspHeatTemplates();
    }

    return () => {
      store.pollIntervalSecs = 0;
      if (labHoursWatchdogRef.current) {
        clearTimeout(labHoursWatchdogRef.current);
      }
    };
  }, []);

  // Runs on MobX-driven re-renders (component is an observer); mirrors the
  // previous class componentDidUpdate behavior without a useEffect.
  if (
    !store.hasLabHours &&
    userLab.desired_state !== 'running' &&
    labHoursWatchdogRef.current
  ) {
    clearTimeout(labHoursWatchdogRef.current);
    labHoursWatchdogRef.current = null;
  }

  if (uiStore.currentLanguage !== store.currentLanguage) {
    store.currentLanguage = uiStore.currentLanguage;
    store.resetLabInformation();
    store.getLabInformation(uiStore.currentLanguage);
  } else if (store.canFetchLabInformation) {
    store.getLabInformation(uiStore.currentLanguage);
  }

  const getComponents = () => {
    if (!userLab.doc_id || !store.hasLabHours) {
      return null;
    }

    return userLab.components?.map((comp) => {
      const ElemComp = componentMap[comp.driver ?? ''] || DefaultLabComponent;
      return (
        <ElemComp
          key={comp.slug}
          action={store.doCommand}
          labComponent={comp}
          labStore={store}
        />
      );
    });
  };

  const renderModalBody = () => {
    if (store.modal.modalType === 'delete') {
      return (
        <Text component={TextVariants.p}>
          <Trans>
            {
              'Deleting your lab will <strong>permanently remove the current lab environment</strong> and any unsaved progress. Are you sure you want to proceed?'
            }
          </Trans>
        </Text>
      );
    }

    if (store.modal.modalType === 'stop') {
      return (
        <Text component={TextVariants.p}>
          <Trans>
            {
              'Stopping your lab will <strong>shut down all running virtual machines</strong>. Your lab data will be preserved. Are you sure you want to proceed?'
            }
          </Trans>
        </Text>
      );
    }

    return null;
  };

  const modalActions = store.modal.confirmText
    ? [
        <Button
          key="confirm"
          variant="primary"
          onClick={() => store.modalDoConfirm()}
          isDisabled={store.modal.working}
          isLoading={store.modal.working}
        >
          {store.modal.confirmText}
        </Button>,
        <Button
          key="cancel"
          variant="link"
          onClick={() => store.hideModal()}
          isDisabled={store.modal.working}
        >
          {store.modal.dismissText}
        </Button>,
      ]
    : [
        <Button
          key="dismiss"
          variant="primary"
          onClick={() => store.hideModal()}
          isDisabled={store.modal.working}
        >
          {store.modal.dismissText}
        </Button>,
      ];

  const modalBody = renderModalBody();

  const labComponents = getComponents();

  const labContent = (
    <>
      {!store.canLabStart ? (
        <div className="lab-card__banner">
          <LabAlert labStore={store} alertType={store.reasonLabCantStart} />
        </div>
      ) : null}
      <LabCardBody>
        <labEnvironmentContext.Provider value={store}>
          <LabInstructions lab={store} user={userStore} />
          <DynamicLaunchParameter labStore={store} />
        </labEnvironmentContext.Provider>
        {userLab.web_applications?.length ? (
          <LabWebApps labStore={store} />
        ) : null}
      </LabCardBody>
      <LabCardFooter>
        <LabControls labStore={store} />
      </LabCardFooter>
      {labComponents?.length ? (
        <LabCardResources>{labComponents}</LabCardResources>
      ) : null}
      {userLab.doc_id && store.hasLabHours ? (
        <LabCardAutoMgmt>
          <AutostopTimer labStore={store} />
        </LabCardAutoMgmt>
      ) : null}
    </>
  );

  return (
    <Provider labStore={store}>
      <LabCard>{labContent}</LabCard>

      <Modal
        variant={ModalVariant.small}
        width="40rem"
        className="lab-confirmation-modal"
        isOpen={store.modal.opened}
        onClose={() => store.hideModal()}
        title={store.modal.title}
        titleIconVariant={store.modal.modalType ? 'warning' : undefined}
        aria-label={store.modal.title}
        showClose
        actions={modalActions}
      >
        {modalBody ? <TextContent>{modalBody}</TextContent> : null}
      </Modal>
    </Provider>
  );
};

export default observer(LabEnvironment);
